import { bbkApiSlice } from '@bbkAdminRedux/rtkq/bbkApi.slice';

/** Platform-level locale exposed by `GET /api/v2/languages/available`. */
export type AvailableLocale = {
  locale_code: string;
  display_name: string;
  rtl: boolean;
  enabled: boolean;
  sort_order: number;
};

/**
 * Locale enabled on an application, returned by `GET /api/v2/apps/{appKey}/languages`.
 *
 * `rule_id` is the hashids-encoded id of the attached `LOCALE_MATCHING` rule (matching the
 * encoding used by other API IDs e.g. `Rule.application_id`). `null` / `undefined` mean the
 * locale currently has no matching rule attached.
 *
 * `client_formatted_rule` is the Brightback expression for the attached rule, included by the
 * languages list endpoint so the matching-rule cell can render on page load without a follow-up
 * fetch. Null / undefined when the locale has no rule (or its rule was deleted / inactivated).
 */
export type ApplicationLocale = {
  locale_code: string;
  base: boolean;
  enabled: boolean;
  rule_id?: string | null;
  client_formatted_rule?: string | null;
};

export type LanguageSourceType = 'API_JS' | 'BILLING';

export type LanguageSourceConfig = {
  source_type: LanguageSourceType;
  field_key: string;
  enabled: boolean;
};

type AppKeyArg = { appKey: string };
type LocaleCodeArg = AppKeyArg & { localeCode: string };

/**
 * Args for the consolidated `PUT /api/v2/apps/{app}/languages/{localeCode}` endpoint.
 * Every field is optional; at least one must be present.
 *
 * - `enabled: false` deactivates the locale.
 * - `enabled: true` (or omitted alongside other fields) ensures the locale row exists & is enabled.
 * - `base: true` promotes the locale to the base / fallback locale.
 * - `clientFormattedRule` (non-empty, only honored when `enabled !== false`) creates or updates
 *   the `LOCALE_MATCHING` rule attached to the locale.
 */
export type UpsertApplicationLocaleArgs = LocaleCodeArg & {
  base?: boolean;
  enabled?: boolean;
  clientFormattedRule?: string;
};

export const languagesSlice = bbkApiSlice.injectEndpoints({
  endpoints: (build) => ({
    getAvailableLocales: build.query<AvailableLocale[], void>({
      query: () => `/api/v2/languages/available`,
    }),
    getApplicationLocales: build.query<ApplicationLocale[], AppKeyArg>({
      query: ({ appKey }) => `/api/v2/apps/${appKey}/languages`,
      providesTags: ['ApplicationLocales'],
    }),
    upsertApplicationLocale: build.mutation<
      ApplicationLocale,
      UpsertApplicationLocaleArgs
    >({
      query: ({ appKey, localeCode, base, enabled, clientFormattedRule }) => ({
        url: `/api/v2/apps/${appKey}/languages/${localeCode}`,
        method: 'PUT',
        body: {
          base,
          enabled,
          client_formatted_rule: clientFormattedRule,
        },
      }),
      invalidatesTags: ['ApplicationLocales'],
    }),
    getLanguageSourceConfig: build.query<
      LanguageSourceConfig | null,
      AppKeyArg
    >({
      query: ({ appKey }) => `/api/v2/apps/${appKey}/language-source`,
      providesTags: ['LanguageSourceConfig'],
    }),
    configureLanguageSource: build.mutation<
      LanguageSourceConfig,
      AppKeyArg & {
        sourceType: LanguageSourceType;
        fieldKey: string;
        enabled: boolean;
      }
    >({
      query: ({ appKey, sourceType, fieldKey, enabled }) => ({
        url: `/api/v2/apps/${appKey}/language-source`,
        method: 'PUT',
        body: {
          source_type: sourceType,
          field_key: fieldKey,
          enabled,
        },
      }),
      invalidatesTags: ['LanguageSourceConfig'],
    }),
  }),
});

export const {
  useGetAvailableLocalesQuery,
  useGetApplicationLocalesQuery,
  useUpsertApplicationLocaleMutation,
  useGetLanguageSourceConfigQuery,
  useConfigureLanguageSourceMutation,
} = languagesSlice;
